import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Registers a stock order, an FX conversion or an OTC order executed outside
 * the platform — booked in 1C, or not booked anywhere yet — so it shows up in
 * our history and dashboards.
 *
 * Backend: `/api/admin/external-orders/*`, see trading-be
 * docs/external-orders.md. What the operator needs to know:
 *  - **An import lands executed.** Stock/FX `filled`, OTC `confirmed`. Nothing
 *    is frozen, nothing reaches GTN, and the client is not notified.
 *  - **`adjustBalance` has no default.** `true` moves our balances the way a
 *    normal execution would; `false` means our balances already reflect the
 *    deal. Guessing wrong double-counts the deal or leaves it out.
 *  - **`syncWith1C` defaults to false.** `true` queues the deal for the 1C sync
 *    — only for deals that are NOT in 1C yet, or 1C books them twice.
 *  - **`syncWithFX` (FX orders only) defaults to false.** `true` queues the
 *    conversion for the FX-system export — only if the FX system does NOT
 *    have it yet.
 *  - **There is no duplicate guard.** Submitting the same deal twice books it
 *    twice.
 *  - **Admin only**, on the backend as well as here.
 */

export type ExternalOrderImportType = 'order' | 'fx_order' | 'otc_order';

/**
 * `processing` that is not in flight means the rows were written but the
 * balance update failed — reconcile it by hand. `failed` was rolled back.
 */
export type ExternalOrderImportStatus = 'processing' | 'completed' | 'failed';

/**
 * `tradeDate` is a date-time with offset (`2026-09-10T14:05:00+04:00`);
 * `settlementDate` is `YYYY-MM-DD`. Amounts are plain numbers.
 */
interface ExternalOrderImportCommon {
  userId: string;
  tradeDate: string;
  adjustBalance: boolean;
  /** Queue the ledger legs for the 1C sync. Default false. */
  syncWith1C?: boolean;
  note?: string;
}

export interface ExternalStockOrderImportPayload
  extends ExternalOrderImportCommon {
  tickerId: string;
  sourceId: string;
  paymentCurrency: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit';
  quantity: number;
  price: number;
  commission: number;
  /** Gross value as booked. Defaults to quantity × price. */
  amount?: number;
  /** Defaults to the exchange calendar's settlement date for tradeDate. */
  settlementDate?: string;
  /** Who holds the units bought / sold; omitted = GTN (docs/custodians.md). */
  custodian?: string;
}

export interface ExternalFxOrderImportPayload
  extends ExternalOrderImportCommon {
  from: string;
  to: string;
  /** Both amounts are stored verbatim — nothing is re-derived from the rate. */
  fromAmount: number;
  toAmount: number;
  rate: number;
  fee?: number;
  /** Queue the conversion for the FX-system export. Default false. */
  syncWithFX?: boolean;
}

export interface ExternalOtcOrderImportPayload
  extends ExternalOrderImportCommon {
  tickerId: string;
  sourceId: string;
  /**
   * The instrument's price currency, or any active fiat currency — the latter
   * needs `paymentRate`.
   */
  paymentCurrency: string;
  /** Price currency per 1 `paymentCurrency` ("1 USD = 7.12 CNY" is 7.12). */
  paymentRate?: number;
  side: 'buy' | 'sell';
  type: 'market' | 'limit';
  quantity: number;
  averagePrice: number;
  commission: number;
  settlementDate: string;
  /**
   * Bonds only: the ACI actually booked — total for the deal, in the price
   * currency. Computed as at `settlementDate` when absent.
   */
  aci?: number;
  /** Required for a listed (non-OTC) instrument: the custodian it was traded through. */
  custodian?: string;
}

/** The ACI an OTC import computes when `aci` is not sent. */
export interface ExternalOtcOrderAci {
  isBond: boolean;
  /** Price currency. */
  currency: string;
  aci: number;
}

export interface ExternalOrderImportUser {
  id: string;
  email: string;
  name: string;
  surname: string;
  brokerageAccount: string;
}

export interface ExternalOrderImport {
  id: string;
  type: ExternalOrderImportType;
  userId: string;
  adminId: string;
  adjustBalance: boolean;
  syncWith1C: boolean;
  /** FX orders only; always false for stock and OTC. */
  syncWithFX: boolean;
  status: ExternalOrderImportStatus;
  orderID: string | null;
  tradeId: string | null;
  tradeDate: string;
  note: string;
  /** Omitted by the list endpoint. */
  request?: Record<string, unknown>;
  error: string | null;
  completedAt: string | null;
  /** How the portfolio history was corrected; null when the balance was not adjusted. */
  snapshotCorrection: ExternalOrderSnapshotCorrection | null;
  created: string;
  updated: string;
  /** List endpoint only. */
  user?: ExternalOrderImportUser | null;
  admin?: { id: string; email: string } | null;
}

export interface ExternalOrderImportResult {
  import: ExternalOrderImport;
  order: Record<string, any>;
  /** Only when the balance was adjusted. */
  snapshotCorrection?: ExternalOrderSnapshotCorrection | null;
}

/**
 * Preview / snapshot correction: see the backend's docs/external-orders-preview.md.
 * Dates named `*Date` without a time are `YYYY-MM-DD` portfolio-calendar days.
 */
export interface ExternalOrderSkippedPosition {
  instrumentInternalId: string;
  /** Quantity held that day. */
  held: number;
  /** The deal. */
  change: number;
  /** Below zero. */
  wouldBe: number;
}

export interface ExternalOrderSkippedSnapshot {
  snapshotDate: string;
  positions: ExternalOrderSkippedPosition[];
}

export type ExternalOrderSnapshotCorrectionStatus =
  | 'completed'
  | 'partial'
  | 'failed';

export interface ExternalOrderSnapshotCorrection {
  status: ExternalOrderSnapshotCorrectionStatus;
  snapshots?: number;
  adjusted?: number;
  /** Always the full count; `skippedSnapshots` holds at most 100 days. */
  skipped?: number;
  firstSnapshotDate?: string | null;
  lastSnapshotDate?: string | null;
  skippedSnapshots?: ExternalOrderSkippedSnapshot[];
  /** `failed` only. */
  error?: string;
  correctedAt: string;
}

export interface ExternalOrderBalanceCheck {
  /** Cash currency (`USD`) or instrument (`AAPL~NSDQ`). */
  currency: string;
  /** Instrument sells: the custodian whose free units were checked (GTN when none named). */
  custodian?: string;
  need: number;
  available: number;
  sufficient: boolean;
}

export interface ExternalOrderPreviewDay {
  snapshotDate: string;
  status: 'adjusted' | 'skipped';
  totalUsdBefore: number;
  totalUsdAfter: number;
  positions: { instrumentInternalId: string; before: number; after: number }[];
}

export interface ExternalOrderPreviewHistory {
  /** False when the import will not correct the history (adjustBalance false). */
  applies: boolean;
  snapshots: number;
  adjusted: number;
  skipped: number;
  rowsAdded: number;
  rowsRemoved: number;
  unpriced: number;
  firstSnapshotDate: string | null;
  lastSnapshotDate: string | null;
  skippedSnapshots: ExternalOrderSkippedSnapshot[];
  days: ExternalOrderPreviewDay[];
}

export type ExternalOrderPayoutEffect =
  | 'none'
  | 'client_owed'
  | 'client_overpaid'
  | 'skipped'
  | 'no_history'
  | 'excluded';

export interface ExternalOrderPayoutImpact {
  type: 'dividend' | 'coupon' | 'adr_fee';
  instrumentInternalId: string;
  eventKey: string | null;
  runId: string | null;
  ledgerId: string | null;
  recordDate: string | null;
  holderDate: string;
  currency: string;
  perShare: string | null;
  taxRatePercent: string;
  paid: boolean;
  paidAmount: number;
  paidShares: number | null;
  excluded: boolean;
  sharesBefore: number | null;
  sharesAfter: number | null;
  sharesDifference: number | null;
  /** From the client's side: positive = client is owed, negative = overpaid. */
  amountDifference: number | null;
  effect: ExternalOrderPayoutEffect;
}

export type ExternalOrderPreviewWarning =
  | 'INSUFFICIENT_BALANCE'
  | 'NO_HISTORY'
  | 'HISTORY_DAYS_SKIPPED'
  | 'PAYOUTS_AFFECTED'
  | 'PAYOUTS_UNVERIFIABLE';

export interface ExternalOrderPreview {
  type: ExternalOrderImportType;
  userId: string;
  tradeDate: string;
  /** History is corrected from the day after it. */
  tradeDay: string;
  adjustBalance: boolean;
  /** False only when adjustBalance is true and a balance check fails. */
  canImport: boolean;
  balanceChecks: ExternalOrderBalanceCheck[];
  delta: {
    instruments: { instrumentInternalId: string; quantity: number }[];
    cash: { currency: string; amount: number }[];
  };
  history: ExternalOrderPreviewHistory;
  /** Always empty for FX orders. */
  payouts: ExternalOrderPayoutImpact[];
  warnings: (ExternalOrderPreviewWarning | string)[];
}

export interface ExternalOrderImportListParams {
  page: number;
  itemsPerPage: number;
  type?: ExternalOrderImportType | '';
  status?: ExternalOrderImportStatus | '';
  userId?: string;
}

export interface ExternalOrderImportListResponse {
  total: number;
  items: ExternalOrderImport[];
}

@Injectable({ providedIn: 'root' })
export class ExternalOrderService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`, so these resolve to `/api/admin/external-orders`. */
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  importOrder(
    payload: ExternalStockOrderImportPayload
  ): Observable<ExternalOrderImportResult> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/orders`,
      payload
    );
  }

  importFxOrder(
    payload: ExternalFxOrderImportPayload
  ): Observable<ExternalOrderImportResult> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/fx-orders`,
      payload
    );
  }

  importOtcOrder(
    payload: ExternalOtcOrderImportPayload
  ): Observable<ExternalOrderImportResult> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/otc-orders`,
      payload
    );
  }

  // Same bodies as the imports; nothing is written.

  previewOrder(
    payload: ExternalStockOrderImportPayload
  ): Observable<ExternalOrderPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/orders/preview`,
      payload
    );
  }

  previewFxOrder(
    payload: ExternalFxOrderImportPayload
  ): Observable<ExternalOrderPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/fx-orders/preview`,
      payload
    );
  }

  previewOtcOrder(
    payload: ExternalOtcOrderImportPayload
  ): Observable<ExternalOrderPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/otc-orders/preview`,
      payload
    );
  }

  estimateOtcAci(params: {
    tickerId: string;
    sourceId: string;
    quantity: number;
    settlementDate: string;
    custodian?: string;
  }): Observable<ExternalOtcOrderAci> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/external-orders/otc-orders/aci`,
      params
    );
  }

  list(
    params: ExternalOrderImportListParams
  ): Observable<ExternalOrderImportListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.type) query['type'] = params.type;
    if (params.status) query['status'] = params.status;
    if (params.userId) query['userId'] = params.userId;
    return this.networkHelper.get(`${this.hostUrl}/admin/external-orders`, {
      ...query,
    });
  }

  getById(id: string): Observable<ExternalOrderImport> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/external-orders/${encodeURIComponent(id)}`
    );
  }
}
